import React, { useEffect, useState, useRef } from 'react';
import { useI18n } from '../useI18n.js';
import logo from '../assets/logo/transparent_logo.png';
import { useGameLogic } from '../hooks/useGameLogic';
import { GameHeader } from '../components/GameHeader';
import { ChatPanel } from '../components/ChatPanel';
import { ChatBox } from '../components/ChatBox';
import { Announcements } from '../components/Announcements';
import { Scores } from '../components/Scores';
import { GuessZone } from '../components/GuessZone';
import { MashupRound } from '../components/MashupRound';
import { ChronoRound } from '../components/ChronoRound';
import { DrawBoard } from '../components/DrawBoard';
import { StudioRoom } from '../components/StudioRoom';
import { DiscussionRound } from '../components/DiscussionRound';
import { WhoRound } from '../components/WhoRound';
import { CoverDrawGame } from '../components/CoverDrawGame';
import { RoundSummaryOverlay } from '../components/RoundSummaryOverlay';
import { GameEndOverlay } from '../components/GameEndOverlay';
import { setGameInProgress } from '../gameState';
import { GAME_MODES } from '../gameModes';
import { QRCodeSVG } from 'qrcode.react';

// Bouton "Partager" : QR de la partie au survol, copie du lien au clic.
function ShareButton() {
  const { t } = useI18n();
  const [hover, setHover] = useState(false);
  const [copied, setCopied] = useState(false);
  const link = typeof window !== 'undefined' ? window.location.href : '';
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* clipboard indispo */ }
  };
  return (
    <div className="share-wrap" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button type="button" onClick={copy}
        style={{ background: 'transparent', color: '#fff', border: '2px solid #fff', padding: '0.4rem 0.8rem', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
        {copied ? `✅ ${t('linkCopied') || 'Copié !'}` : `🔗 ${t('share') || 'Partager'}`}
      </button>
      {hover && (
        <div className="share-tooltip">
          <div className="share-qr"><QRCodeSVG value={link || ' '} size={150} marginSize={2} bgColor="#ffffff" fgColor="#111827" /></div>
          <div className="share-tip-txt">{t('shareHint') || 'Scanne, ou clique pour copier le lien'}</div>
        </div>
      )}
    </div>
  );
}

// Resume lisible des types de media autorises (footer parametres)
function mediaSummary(am) {
  if (!am) return 'Tous médias';
  const parts = [];
  if (am.text) parts.push('Texte');
  if (am.image) parts.push('Image');
  if (am.gif) parts.push('Gif');
  if (am.sound) parts.push('Son');
  return parts.length ? parts.join(', ') : 'Tous médias';
}

export default function GamePage({ roomCode, roomSecret, pseudo, onLeave }) {
  const { t } = useI18n();
  const [roomParams, setRoomParams] = useState({
    mode: null,
    rounds: 10,
    onlyGifs: false,
    // Types de media autorises en jeu
    allowedMedia: { text: true, image: true, gif: true, sound: true },
    messagesPerRound: 1,
    minMessageLength: 20,
    // nouveau: temps de réflexion (secondes)
    thinkingTimeSec: 5,
    // nouveau: option Messages à la suite (sélection consécutive des messages)
    messagesAlaSuite: true,
    // Periode (ms epoch) optionnelle
    periodAfter: null,
    periodBefore: null,
    // modes audio ("mashup"/"blindtest")
    clipSeconds: 15,
    extractionModes: { start: true, middle: true, random: true },
    allowedMashupAuthors: [],
    blindtestAttempts: 2,
    drawEnabled: false,
    // mode "chrono"
    chronoThinkSec: 20,
    // mode "atelier"
    createSec: 180,
    rateSec: 45,
    // mode "discussion" (Fibbage)
    writeSec: 45,
    voteSec: 30,
    // mode "events" (remise en ordre)
    eventsSec: 45,
    // mode "whoisit" (retrouver le prenom)
    whoSec: 35,
    // mode "coverDraw" (dessine une cover)
    coverDrawSec: 240,
    // mode "studio" (recreer un mashup)
    studioLoopSec: 15,
    studioSameMashup: false,
  });
  const hasJoinedRef = useRef(false);

  const {
    connected,
    gameStarted,
    isChef,
    chefName,
    roundNumber,
    phase,
    timeLeft,
  guessOptions,
  guessAvatars,
  guessNames,
    hasGuessed,
    myGuess,
    scores,
    playersGuessed,
    clipUrl,
    clipDurationSec,
    guessTicks,
    clipSync,
    clipProgress,
    lastMashupName,
    lastMashupAuthor,
    lastCorrectAnswer,
    chronoMessage,
    chronoMessages,
    chronoPeriod,
    lastAnswerFraction,
    blindtestPool,
    blindtestMaxAttempts,
    drawEnabled,
    emitDrawSegment,
    drawClearInfo,
    emitDrawClearVote,
    modeAccess,
    studioAssign,
    studioInfo,
    studioItems,
    studioRanking,
    creationProgress,
    submitCreation,
    submitRating,
    discussionContext,
    discussionOptions,
    discussionReveal,
    discussionProgress,
    submitLie,
    submitVote,
    whoContext,
    whoKey,
    whoReveal,
    coverAssign,
    coverDraw,
    coverReveal,
    coverResult,
    coverUpvote,
    submitCover,
    submitUpvote,
    lobbyMode,
    selectMode,
    messages,
    announcements,
    lastAuthor,
    lastProposals,
    lastRoundPoints,
    gameSettings,
    currentRoom,
    joinRoom,
    startGame,
    stopGame,
    submitGuess,
    restartLobby,
    leaveRoom,
    finalRanking,
    chatMessages,
    sendChatMessage,
    datasetReady,
    datasetInfo,
    datasetAuthors,
    detectedNames,
    keptAuthors,
    setKeptAuthors,
    maxAuthors,
    datasetPeriod,
    sources,
    removeSource,
    eloChanges,
    uploadCsv,
    scrapeDiscord,
    uploading,
    uploadProgress,
  } = useGameLogic(pseudo);

  // Signale "partie en cours" (masque le badge profil pour empecher le logout mid-game)
  useEffect(() => {
    setGameInProgress(gameStarted && phase !== 'ended');
    return () => setGameInProgress(false);
  }, [gameStarted, phase]);

  // Theme couleur par mode (classic=vert, mashup=orange) : applique sur <html>,
  // les variables CSS --color-* sont surchargees en lobby ET en partie.
  const activeMode = gameStarted ? (gameSettings?.mode || null) : lobbyMode;
  useEffect(() => {
    const el = document.documentElement;
    if (activeMode) el.setAttribute('data-tgr-mode', activeMode);
    else el.removeAttribute('data-tgr-mode');
    return () => el.removeAttribute('data-tgr-mode');
  }, [activeMode]);

  // join room automatically once
  useEffect(() => {
    if (!hasJoinedRef.current && roomCode && pseudo) {
      joinRoom({ roomCode, pseudo, secret: roomSecret });
      hasJoinedRef.current = true;
    }
  }, [roomCode, roomSecret, pseudo, joinRoom]);

  const handleLeave = () => {
    leaveRoom();
    hasJoinedRef.current = false;
  };

  const [drawTab, setDrawTab] = useState(false); // onglet Dessin (partage l'encart chat)
  // État du chat overlay (mobile) + badge "nouveau message"
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(false);
  const lastSeenChatLenRef = useRef(0);
  useEffect(() => {
    const len = chatMessages?.length || 0;
    // Si overlay fermé et nouveaux messages → activer le badge (1)
    if (!chatOpen && len > lastSeenChatLenRef.current) {
      setChatUnread(true);
    }
    // Mémoriser la longueur courante
    lastSeenChatLenRef.current = len;
  }, [chatMessages, chatOpen]);
  const openChat = () => { setChatOpen(true); setChatUnread(false); };
  const closeChat = () => setChatOpen(false);

  const overlayVisible = phase === 'result' || phase === 'transition';
  const gameMode = gameSettings?.mode;
  const isAudioGame = gameMode === 'mashup' || gameMode === 'blindtest';
  const isChronoGame = gameMode === 'chrono';
  const isCreationGame = gameMode === 'studio';
  const isDiscussionGame = gameMode === 'discussion';
  const isWhoGame = gameMode === 'whoisit';
  const isCoverDrawGame = gameMode === 'coverDraw';
  const modeMeta = GAME_MODES.find((m) => m.id === gameMode);
  const isCustomGame = isAudioGame || isChronoGame || isCreationGame || isDiscussionGame || isWhoGame || isCoverDrawGame; // ecran dedie

  // Ajouter/retirer une classe globale pour surélever l'input du chat au-dessus des overlays
  useEffect(() => {
    const root = document.documentElement;
    if (overlayVisible) root.classList.add('overlay-open');
    else root.classList.remove('overlay-open');
    return () => root.classList.remove('overlay-open');
  }, [overlayVisible]);

  // Saisie par défaut: rediriger les frappes clavier vers l'input du chat si l'utilisateur tape hors champ
  useEffect(() => {
    const onKeyDown = (e) => {
      const t = e.target;
      const isFormField = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
      if (isFormField) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (!e.key) return;
      // N'intercepter que les caractères imprimables (y compris espace)
      const isPrintable = e.key.length === 1;
      if (!isPrintable) return;
      // Trouver un input de chat visible
      const candidate = document.querySelector('.chat-input input, .chat-input-floating input');
      if (!candidate) return;
      // Vérifier visibilité
      const style = window.getComputedStyle(candidate);
      const visible = style.display !== 'none' && style.visibility !== 'hidden' && candidate.offsetParent !== null;
      if (!visible) return;
      // Empêcher la frappe d'aller ailleurs, puis demander au ChatBox d'insérer le texte
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('chatbox-insert-text', { detail: { text: e.key } }));
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  // Loading screen while connecting
  if (!connected) {
    return <div className="container">{t('connecting')}</div>;
  }

  if (!gameStarted) {
    return (
      <div className="container game-page" style={{ position: 'relative', paddingBottom: isChef ? '84px' : undefined }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <img src={logo} alt={t('appTitle')} style={{ width: 64, height: 64, imageRendering: 'pixelated' }} />
          <h1 style={{ margin: 0 }}>{`${t('room')} #${currentRoom}`}</h1>
        </div>

        {/* Titre du mode choisi par le chef : grand, chevauche le container, synchro live */}
        {lobbyMode && (
          <div className="lobby-mode-title" key={lobbyMode}>{t(`mode_${lobbyMode}_name`)}</div>
        )}

        <button
          className="btn-quit"
          onClick={() => {
            if (window.confirm(t('confirmQuit'))) {
              handleLeave();
              if (onLeave) onLeave();
              window.location.reload();
            }
          }}
          style={{ position: 'absolute', top: '3.4rem', right: '1rem' }}
        >
          {t('quit')}
        </button>

        <GameHeader
          roundNumber={roundNumber}
          phase={phase}
          timeLeft={timeLeft}
          isChef={isChef}
          gameStarted={gameStarted}
          roomParams={roomParams}
          setRoomParams={setRoomParams}
          onSelectMode={selectMode}
          modeAccess={modeAccess}
          onUploadCsv={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const text = await file.text();
            uploadCsv(text, file.name);
          }}
          datasetReady={datasetReady}
          datasetInfo={datasetInfo}
          datasetAuthors={datasetAuthors}
          keptAuthors={keptAuthors}
          onKeptAuthors={setKeptAuthors}
          maxAuthors={maxAuthors}
          datasetPeriod={datasetPeriod}
          sources={sources}
          onRemoveSource={removeSource}
          onDiscordScrape={scrapeDiscord}
          uploading={uploading}
          uploadProgress={uploadProgress}
        />

        <div className="row-wrap lobby-layout" style={{ gap: '1rem', marginTop: '1rem' }}>
          <ChatBox messages={chatMessages} onSend={sendChatMessage} floatingInput />
          <div className="sidebar">
            {/* En lobby: afficher Annonces au lieu de Scores */}
            <Announcements announcements={announcements} />
          </div>
        </div>

        {isChef && (() => {
          const isAudio = roomParams.mode === 'mashup' || roomParams.mode === 'blindtest';
          const isCreation = roomParams.mode === 'studio';
          const isCover = roomParams.mode === 'coverDraw';
          const startReady = isAudio
            ? (roomParams.allowedMashupAuthors?.length || 0) >= 2
            : (isCreation || isCover) ? true : datasetReady;
          const hint = isAudio ? t('mashupAuthorsHint') : isCreation ? (t('atelierReadyHint') || 'Prêt à créer !') : isCover ? (t('mode_coverDraw_desc')) : t('importCsvHint');
          return (
            <div className="cta-start-bar">
              <div className="cta-content">
                <div>
                  {!startReady ? (
                    <span className="cta-hint">{hint}</span>
                  ) : (
                    <span className="cta-ok">{isAudio ? t('mashupReady') : isCreation ? (t('atelierReadyHint') || 'Prêt à créer !') : isCover ? (t('mode_coverDraw_name') || 'Cover') : t('datasetReady')}</span>
                  )}
                </div>
                <button
                  className="cta-start-button"
                  onClick={() => {
                    if (!roomParams.mode) { alert(t('selectModeFirst')); return; }
                    if (!startReady) { alert(hint); return; }
                    startGame(roomParams);
                  }}
                  disabled={!startReady || !roomParams.mode}
                  title={!roomParams.mode ? t('selectModeFirst') : (!startReady ? hint : undefined)}
                >
                  🚀 {t('startGame')}
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  return (
    <div className="container game-page" style={{ position: 'relative' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
      <img src={logo} alt={t('appTitle')} style={{ width: 64, height: 64, imageRendering: 'pixelated' }} />
      <h1 style={{ margin: 0 }}>{gameStarted ? t('appTitle') : `${t('room')} #${currentRoom}`}</h1>
    </div>
      {/* Grand titre du mode en jeu (meme format que le lobby) */}
      {gameStarted && gameMode && (
        <div className="lobby-mode-title" key={gameMode}>{t(`mode_${gameMode}_name`)}</div>
      )}
      {roomSecret && (
        <div style={{ position: 'absolute', top: '3.5rem', right: '1rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          {t('protectedLink')}
        </div>
      )}

      <div style={{ position: 'absolute', top: '3.4rem', right: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center', zIndex: 20 }}>
        <ShareButton />
        {isChef && gameStarted && phase && phase !== 'ended' && (
          <button
            type="button"
            onClick={() => { if (window.confirm('Arrêter la partie en cours ?')) stopGame(); }}
            style={{ background: 'transparent', color: gameMode === 'studio' ? '#fff' : '#ef4444', border: `2px solid ${gameMode === 'studio' ? '#fff' : '#ef4444'}`, padding: '0.4rem 0.8rem', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
          >
            Arrêter la partie
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            if (window.confirm(t('confirmQuit'))) {
              handleLeave();
              if (onLeave) onLeave();
              window.location.reload();
            }
          }}
          style={{ background: '#ef4444', color: '#fff', border: '2px solid #ef4444', padding: '0.4rem 0.8rem', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
        >
          {t('quit')}
        </button>
      </div>

      <GameHeader
        roundNumber={roundNumber}
        phase={phase}
        timeLeft={timeLeft}
        isChef={isChef}
        gameStarted={gameStarted}
        roomParams={roomParams}
        setRoomParams={setRoomParams}
        onUploadCsv={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const text = await file.text();
          uploadCsv(text);
        }}
        datasetReady={datasetReady}
        datasetInfo={datasetInfo}
        datasetAuthors={datasetAuthors}
        detectedNames={detectedNames}
        keptAuthors={keptAuthors}
        onKeptAuthors={setKeptAuthors}
        maxAuthors={maxAuthors}
        datasetPeriod={datasetPeriod}
        sources={sources}
        onRemoveSource={removeSource}
        onDiscordScrape={scrapeDiscord}
        uploading={uploading}
        uploadProgress={uploadProgress}
      />

      <div className="row-wrap game-layout" style={{ gap: '1rem', marginTop: '1rem' }}>
        <div className="game-main">
          {isAudioGame ? (
            <MashupRound
              mode={gameMode}
              phase={phase}
              clipUrl={clipUrl}
              clipDurationSec={clipDurationSec}
              clipSync={clipSync}
              clipProgress={clipProgress}
              mashupName={lastMashupName}
              mashupAuthor={lastMashupAuthor}
              correctAnswer={lastCorrectAnswer}
              searchPool={blindtestPool}
              maxAttempts={blindtestMaxAttempts}
              guessOptions={guessOptions}
              guessNames={guessNames}
              guessAvatars={guessAvatars}
              hasGuessed={hasGuessed}
              selectedGuess={myGuess}
              onGuess={submitGuess}
              guessTicks={guessTicks}
              proposals={lastProposals}
              roundPoints={lastRoundPoints}
              myPseudo={pseudo}
            />
          ) : isChronoGame ? (
            <ChronoRound
              phase={phase}
              message={chronoMessage}
              messages={chronoMessages}
              period={chronoPeriod}
              timeLeft={timeLeft}
              hasGuessed={hasGuessed}
              onGuess={submitGuess}
              guessTicks={guessTicks}
              answerFraction={lastAnswerFraction}
              proposals={lastProposals}
              roundPoints={lastRoundPoints}
              myPseudo={pseudo}
            />
          ) : isCreationGame ? (
            <StudioRoom
              phase={phase}
              assign={studioAssign}
              info={studioInfo}
              items={studioItems}
              ranking={studioRanking}
              timeLeft={timeLeft}
              onSubmitCreation={submitCreation}
              onRate={submitRating}
              roomCode={roomCode}
            />
          ) : isDiscussionGame ? (
            <DiscussionRound
              phase={phase}
              context={discussionContext}
              options={discussionOptions}
              reveal={discussionReveal}
              timeLeft={timeLeft}
              submitLie={submitLie}
              submitVote={submitVote}
              progress={discussionProgress}
              myPseudo={pseudo}
            />
          ) : isWhoGame ? (
            <WhoRound
              phase={phase}
              context={whoContext}
              keyMsg={whoKey}
              reveal={whoReveal}
              timeLeft={timeLeft}
              onSubmit={submitGuess}
              progress={playersGuessed}
              myPseudo={pseudo}
            />
          ) : isCoverDrawGame ? (
            <CoverDrawGame
              phase={phase}
              assign={coverAssign}
              draw={coverDraw}
              reveal={coverReveal}
              result={coverResult}
              upvote={coverUpvote}
              timeLeft={timeLeft}
              myPseudo={pseudo}
              onSubmitCover={submitCover}
              onGuess={submitGuess}
              onUpvote={submitUpvote}
            />
          ) : (
            <>
              <ChatPanel messages={messages} />
              <div className="guess-zone">
                <GuessZone
                  phase={phase}
                  guessOptions={guessOptions}
                  hasGuessed={hasGuessed}
                  selectedGuess={myGuess}
                  onGuess={submitGuess}
                  avatarMap={guessAvatars}
                  nameMap={guessNames}
                />
              </div>
            </>
          )}
        </div>

        <div className="sidebar">
          <Scores scores={scores} chefName={chefName} guessedPlayers={playersGuessed} />
          {/* Cacher le chat de la sidebar lorsque l'overlay est ouvert (mobile) */}
          <div style={{ display: chatOpen ? 'none' : undefined }}>
            {drawEnabled ? (
              <div className="chatdraw">
                <div className="chatdraw-tabs">
                  <button type="button" className={`cd-tab${!drawTab ? ' active' : ''}`} onClick={() => setDrawTab(false)}>{t('chat')}</button>
                  <button type="button" className={`cd-tab cd-draw${drawTab ? ' active' : ''}`} onClick={() => setDrawTab(true)}>{t('drawTab') || 'Dessin'}</button>
                </div>
                <div style={{ display: drawTab ? 'none' : undefined }}>
                  <ChatBox messages={chatMessages} onSend={sendChatMessage} showHeader={false} />
                </div>
                {drawTab && <DrawBoard onSegment={emitDrawSegment} onClearVote={emitDrawClearVote} clearInfo={drawClearInfo} />}
              </div>
            ) : (
              <ChatBox messages={chatMessages} onSend={sendChatMessage} />
            )}
          </div>
        </div>
      </div>


      {!isCustomGame && (
        <RoundSummaryOverlay
          visible={overlayVisible}
          author={lastAuthor}
          scores={scores}
          proposals={lastProposals}
          roundPoints={lastRoundPoints}
          myPseudo={pseudo}
          messages={messages}
          avatarMap={guessAvatars}
          nameMap={guessNames}
        />
      )}

      <GameEndOverlay
  visible={phase === 'ended'}
        ranking={finalRanking}
        eloChanges={eloChanges}
        showDrawing={drawEnabled}
        isChef={isChef}
        onRestart={restartLobby}
        onQuit={() => {
          handleLeave();
          if (onLeave) onLeave();
          window.location.reload();
        }}
      />

      {gameStarted && gameSettings && !isCustomGame && (
        <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
          {currentRoom && <span>{t('room')} {currentRoom} – </span>}
          {t('settings')}: {gameSettings.roundsTotal} {t('roundsNoun')} – {gameSettings.messagesPerRound} {t('messagesPerRoundNoun')} – {mediaSummary(gameSettings.allowedMedia)} – Pav-o-meter {gameSettings.minMessageLength} – {gameSettings.sequentialMessages ? t('sequentialMessages') : t('randomMessages')}
        </div>
      )}
      {gameStarted && gameSettings && isCustomGame && (
        <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
          {currentRoom && <span>{t('room')} {currentRoom} – </span>}
          {t('settings')}: {gameSettings.roundsTotal} {t('roundsNoun')}{isAudioGame ? ` – ${gameSettings.clipSeconds}s` : ''}
        </div>
      )}

      {/* Bouton flottant de chat (mobile). Visible via CSS @media */}
      <button
        type="button"
        className="chat-fab"
        aria-label={t('openChat')}
        onClick={openChat}
      >
        💬
        {chatUnread && <span className="badge">1</span>}
      </button>

      {/* Overlay de chat (mobile) */}
      {chatOpen && (
        <div className="chat-overlay" role="dialog" aria-modal="true" onClick={closeChat}>
          <div className="chat-sheet" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>{t('chat')}</h3>
              <button onClick={closeChat} aria-label={t('closeChat')}>{t('close')}</button>
            </div>
            <ChatBox messages={chatMessages} onSend={sendChatMessage} floatingInput showHeader={false} />
          </div>
        </div>
      )}
    </div>
  );
}
